// frontend/src/components/StoryCharactersPanel.js
// NextScenes Story Characters / Character Bible panel
//
// Purpose:
// - Let the story owner/admin add and maintain character profiles.
// - Preserve continuity for human writers first.
// - Later provide disciplined context for AI scene proposals and character POV help.
// - No hard delete. Characters may only be archived/restored.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api";

const EMPTY_FORM = {
  name: "",
  role: "",
  physicalDescription: "",
  personality: "",
  values: "",
  weaknesses: "",
  speechStyle: "",
  relationships: "",
  backstory: "",
  goals: "",
  fears: "",
  characterArc: "",
  importantRules: "",
  notes: "",
};

function cleanId(story) {
  return String(story?._id || story?.id || "").trim();
}

function isFrench(lang) {
  return String(lang || "en").toLowerCase().startsWith("fr");
}

function getUserId(user) {
  return String(user?.id || user?._id || user?.userId || "").trim();
}

function isOwner(story, user) {
  if (!story || !user) return false;

  const uid = getUserId(user);
  const username = String(user?.username || "").trim().toLowerCase();

  const storyAuthorId = String(story?.authorId || "").trim();
  const storyAuthorUsername = String(story?.authorUsername || "").trim().toLowerCase();
  const createdBy = String(story?.createdBy || "").trim().toLowerCase();
  const legacyAuthor = String(story?.author || "").trim().toLowerCase();

  if (uid && storyAuthorId && uid === storyAuthorId) return true;
  if (username && storyAuthorUsername && username === storyAuthorUsername) return true;
  if (username && createdBy && username === createdBy) return true;
  if (username && legacyAuthor && username === legacyAuthor) return true;

  return false;
}

function canManage(story, user) {
  const role = String(user?.role || "").trim().toLowerCase();
  return role === "admin" || isOwner(story, user);
}

function fieldLabel(key, fr) {
  const en = {
    name: "Name",
    role: "Role",
    physicalDescription: "Physical description",
    personality: "Personality",
    values: "Values",
    weaknesses: "Weaknesses",
    speechStyle: "Speech style",
    relationships: "Relationships",
    backstory: "Backstory",
    goals: "Goals",
    fears: "Fears",
    characterArc: "Character arc",
    importantRules: "Important rules",
    notes: "Notes",
  };

  const frLabels = {
    name: "Nom",
    role: "Rôle",
    physicalDescription: "Description physique",
    personality: "Personnalité",
    values: "Valeurs",
    weaknesses: "Faiblesses",
    speechStyle: "Style de parole",
    relationships: "Relations",
    backstory: "Passé du personnage",
    goals: "Objectifs",
    fears: "Peurs",
    characterArc: "Évolution du personnage",
    importantRules: "Règles importantes",
    notes: "Notes",
  };

  return fr ? frLabels[key] || key : en[key] || key;
}

function fieldHelp(key, fr) {
  const en = {
    physicalDescription: "Appearance, age, clothing, marks, posture, or visual details worth preserving.",
    personality: "Temperament, habits, virtues, flaws, and how this character usually behaves.",
    values: "What the character believes in and refuses to betray.",
    weaknesses: "Where the character struggles or can be tempted into error.",
    speechStyle: "How the character speaks: simple, formal, playful, quiet, wise, direct, etc.",
    relationships: "Family, friends, rivals, mentors, community ties, and emotional bonds.",
    backstory: "Important past events that shape the character.",
    goals: "What the character wants or seeks in this story.",
    fears: "What the character avoids, worries about, or must overcome.",
    characterArc: "How the character may grow, learn, or change.",
    importantRules: "Rules collaborators and AI must not break.",
    notes: "Extra details that do not fit elsewhere.",
  };

  const frHelp = {
    physicalDescription: "Apparence, âge, vêtements, marques, posture ou détails visuels à préserver.",
    personality: "Tempérament, habitudes, qualités, défauts et comportement habituel.",
    values: "Ce que le personnage croit et refuse de trahir.",
    weaknesses: "Les points faibles, luttes ou tentations du personnage.",
    speechStyle: "Sa façon de parler : simple, formelle, joyeuse, calme, sage, directe, etc.",
    relationships: "Famille, amis, rivaux, mentors, communauté et liens émotionnels.",
    backstory: "Événements passés importants qui façonnent le personnage.",
    goals: "Ce que le personnage veut ou cherche dans cette histoire.",
    fears: "Ce que le personnage évite, craint ou doit surmonter.",
    characterArc: "Comment le personnage peut grandir, apprendre ou changer.",
    importantRules: "Règles que les collaborateurs et l’IA ne doivent pas briser.",
    notes: "Autres détails utiles.",
  };

  return fr ? frHelp[key] || "" : en[key] || "";
}

const CHARACTER_LIST_PAGE_SIZE = 8;

const LONG_FIELDS = [
  "physicalDescription",
  "personality",
  "values",
  "weaknesses",
  "speechStyle",
  "relationships",
  "backstory",
  "goals",
  "fears",
  "characterArc",
  "importantRules",
  "notes",
];

function characterSearchText(character) {
  return [
    character?.name,
    character?.role,
    character?.physicalDescription,
    character?.personality,
    character?.values,
    character?.weaknesses,
    character?.speechStyle,
    character?.relationships,
    character?.backstory,
    character?.goals,
    character?.fears,
    character?.characterArc,
    character?.importantRules,
    character?.notes,
    character?.status,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function CompactField({ label, help, children }) {
  return (
    <label style={{ display: "grid", gap: 6 }}>
      <span style={{ fontWeight: 700, color: "#334155" }}>{label}</span>
      {help ? (
        <span className="ns-card-meta" style={{ margin: 0, lineHeight: 1.35 }}>
          {help}
        </span>
      ) : null}
      {children}
    </label>
  );
}

function CharacterCard({ character, fr, onEdit, onArchive, onRestore, canEdit }) {
  const archived = String(character?.status || "").toLowerCase() === "archived";

  return (
    <article
      className="ns-card ns-card-soft"
      style={{
        marginBottom: 10,
        borderColor: archived ? "#e5e7eb" : "#bbf7d0",
        background: archived ? "#f8fafc" : "#f0fdf4",
      }}
    >
      <div
        style={{
          display: "flex",
          gap: 12,
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <div style={{ minWidth: 0 }}>
          <h4 style={{ margin: "0 0 4px", color: "#0f172a" }}>
            {character.name || (fr ? "Personnage sans nom" : "Unnamed character")}
          </h4>
          <p className="ns-card-meta" style={{ margin: 0 }}>
            {character.role || (fr ? "Rôle non défini" : "Role not defined")}
            {archived ? ` · ${fr ? "Archivé" : "Archived"}` : ""}
          </p>
        </div>

        {canEdit ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "flex-end" }}>
            <button type="button" className="ns-button ns-button-outline" onClick={() => onEdit(character)}>
              {fr ? "Modifier" : "Edit"}
            </button>

            {archived ? (
              <button type="button" className="ns-button ns-button-outline" onClick={() => onRestore(character)}>
                {fr ? "Restaurer" : "Restore"}
              </button>
            ) : (
              <button type="button" className="ns-button ns-button-outline" onClick={() => onArchive(character)}>
                {fr ? "Archiver" : "Archive"}
              </button>
            )}
          </div>
        ) : null}
      </div>

      {character.personality ? (
        <p style={{ margin: "10px 0 0", color: "#334155", lineHeight: 1.5 }}>
          <strong>{fr ? "Personnalité" : "Personality"}:</strong> {character.personality}
        </p>
      ) : null}

      {character.importantRules ? (
        <p style={{ margin: "8px 0 0", color: "#334155", lineHeight: 1.5 }}>
          <strong>{fr ? "Règles importantes" : "Important rules"}:</strong> {character.importantRules}
        </p>
      ) : null}
    </article>
  );
}

export default function StoryCharactersPanel({
  story,
  user,
  lang = "en",
  onClose,
  canManageOverride = false,
}) {
  const fr = isFrench(lang);
  const storyId = cleanId(story);
  const mayManage = Boolean(canManageOverride) || canManage(story, user);
  const formRef = useRef(null);

  const [characters, setCharacters] = useState([]);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [visibleLimit, setVisibleLimit] = useState(CHARACTER_LIST_PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const activeCharacters = useMemo(
    () => characters.filter((c) => String(c?.status || "active").toLowerCase() !== "archived"),
    [characters]
  );

  const archivedCharacters = useMemo(
    () => characters.filter((c) => String(c?.status || "active").toLowerCase() === "archived"),
    [characters]
  );

  const visibleCharacters = useMemo(() => {
    const q = String(searchText || "").trim().toLowerCase();

    return characters.filter((character) => {
      const archived = String(character?.status || "").toLowerCase() === "archived";
      if (archived && !includeArchived) return false;
      if (!q) return true;
      return characterSearchText(character).includes(q);
    });
  }, [characters, includeArchived, searchText]);

  const listedCharacters = useMemo(
    () => visibleCharacters.slice(0, visibleLimit),
    [visibleCharacters, visibleLimit]
  );

  const hiddenCharacterCount = Math.max(0, visibleCharacters.length - listedCharacters.length);

  useEffect(() => {
    setVisibleLimit(CHARACTER_LIST_PAGE_SIZE);
  }, [searchText, includeArchived, storyId]);

  async function loadCharacters() {
    if (!storyId) return;

    setLoading(true);
    setError("");

    try {
      const data = await api.get(
        `/api/stories/${encodeURIComponent(storyId)}/characters?includeArchived=true`
      );
      setCharacters(Array.isArray(data?.characters) ? data.characters : []);
    } catch (err) {
      setError(err?.message || (fr ? "Impossible de charger les personnages." : "Could not load characters."));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadCharacters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storyId]);

  function updateField(key, value) {
    setForm((cur) => ({ ...cur, [key]: value }));
  }

  function resetForm() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setError("");
    setMessage("");
  }

  function startEdit(character) {
    setEditing(character);
    setForm({
      name: character?.name || "",
      role: character?.role || "",
      physicalDescription: character?.physicalDescription || "",
      personality: character?.personality || "",
      values: character?.values || "",
      weaknesses: character?.weaknesses || "",
      speechStyle: character?.speechStyle || "",
      relationships: character?.relationships || "",
      backstory: character?.backstory || "",
      goals: character?.goals || "",
      fears: character?.fears || "",
      characterArc: character?.characterArc || "",
      importantRules: character?.importantRules || "",
      notes: character?.notes || "",
    });
    setError("");
    setMessage("");

    setTimeout(() => {
      if (formRef.current && typeof formRef.current.scrollIntoView === "function") {
        formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 50);
  }

  async function saveCharacter(event) {
    event.preventDefault();

    if (!storyId) {
      setError(fr ? "Aucune histoire sélectionnée." : "No story selected.");
      return;
    }

    if (!form.name.trim()) {
      setError(fr ? "Le nom du personnage est obligatoire." : "Character name is required.");
      return;
    }

    setSaving(true);
    setError("");
    setMessage("");

    try {
      if (editing?.id || editing?._id) {
        const characterId = String(editing.id || editing._id);
        await api.put(
          `/api/stories/${encodeURIComponent(storyId)}/characters/${encodeURIComponent(characterId)}`,
          form
        );
        setMessage(fr ? "Personnage mis à jour." : "Character updated.");
      } else {
        await api.post(`/api/stories/${encodeURIComponent(storyId)}/characters`, form);
        setMessage(fr ? "Personnage ajouté." : "Character added.");
      }

      resetForm();
      await loadCharacters();
    } catch (err) {
      setError(err?.message || (fr ? "Impossible d’enregistrer le personnage." : "Could not save character."));
    } finally {
      setSaving(false);
    }
  }

  async function archiveCharacter(character) {
    if (!storyId || !(character?.id || character?._id)) return;

    const ok = window.confirm(
      fr
        ? "Archiver ce personnage ? Il ne sera pas supprimé définitivement."
        : "Archive this character? It will not be permanently deleted."
    );
    if (!ok) return;

    setSaving(true);
    setError("");
    setMessage("");

    try {
      const characterId = String(character.id || character._id);
      await api.patch(
        `/api/stories/${encodeURIComponent(storyId)}/characters/${encodeURIComponent(characterId)}/archive`,
        {}
      );
      setMessage(fr ? "Personnage archivé." : "Character archived.");
      await loadCharacters();
    } catch (err) {
      setError(err?.message || (fr ? "Impossible d’archiver le personnage." : "Could not archive character."));
    } finally {
      setSaving(false);
    }
  }

  async function restoreCharacter(character) {
    if (!storyId || !(character?.id || character?._id)) return;

    setSaving(true);
    setError("");
    setMessage("");

    try {
      const characterId = String(character.id || character._id);
      await api.patch(
        `/api/stories/${encodeURIComponent(storyId)}/characters/${encodeURIComponent(characterId)}/restore`,
        {}
      );
      setMessage(fr ? "Personnage restauré." : "Character restored.");
      await loadCharacters();
    } catch (err) {
      setError(err?.message || (fr ? "Impossible de restaurer le personnage." : "Could not restore character."));
    } finally {
      setSaving(false);
    }
  }

  if (!storyId) {
    return (
      <div className="ns-card ns-card-soft">
        <p className="ns-card-meta">{fr ? "Choisissez d’abord une histoire." : "Choose a story first."}</p>
      </div>
    );
  }

  return (
    <div className="ns-card" style={{ borderRadius: 22 }}>
      <div
        style={{
          display: "flex",
          gap: 12,
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <div>
          <p className="ns-card-kicker" style={{ margin: 0 }}>
            {fr ? "Bible des personnages" : "Character Bible"}
          </p>
          <h3 style={{ margin: "4px 0 6px", color: "#0f172a" }}>
            {fr ? "Personnages de l’histoire" : "Story Characters"}
          </h3>
          <p className="ns-card-meta" style={{ margin: 0 }}>
            {fr
              ? "Définissez les personnages pour préserver la continuité et guider les futurs outils IA."
              : "Define characters to preserve continuity and guide future AI assistance."}
          </p>
        </div>

        {onClose ? (
          <button type="button" className="ns-button ns-button-outline" onClick={onClose}>
            {fr ? "Fermer" : "Close"}
          </button>
        ) : null}
      </div>

      <section className="ns-card ns-card-soft" style={{ marginTop: 14 }}>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 12,
            alignItems: "end",
            justifyContent: "space-between",
          }}
        >
          <div style={{ flex: "1 1 280px" }}>
            <label style={{ display: "grid", gap: 6 }}>
              <span style={{ fontWeight: 700, color: "#334155" }}>
                {fr ? "Rechercher un personnage" : "Find a character"}
              </span>
              <input
                className="ns-input"
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                placeholder={fr ? "Nom, rôle, personnalité, règle…" : "Name, role, personality, rule…"}
              />
            </label>
          </div>

          <label
            className="ns-card-meta"
            style={{ display: "inline-flex", gap: 8, alignItems: "center", margin: 0 }}
          >
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(event) => setIncludeArchived(event.target.checked)}
            />
            {fr ? "Afficher les archivés" : "Show archived"}
          </label>
        </div>

        <p className="ns-card-meta" style={{ margin: "10px 0 0" }}>
          {loading
            ? fr
              ? "Chargement…"
              : "Loading…"
            : `${activeCharacters.length} ${fr ? "actif(s)" : "active"} · ${archivedCharacters.length} ${
                fr ? "archivé(s)" : "archived"
              } · ${visibleCharacters.length} ${fr ? "affiché(s)" : "shown"}`}
        </p>
      </section>

      {error ? (
        <div className="ns-card ns-card-soft" style={{ marginTop: 12, borderColor: "#fecaca", color: "#991b1b" }}>
          {error}
        </div>
      ) : null}

      {message ? (
        <div className="ns-card ns-card-soft" style={{ marginTop: 12, borderColor: "#bbf7d0", color: "#166534" }}>
          {message}
        </div>
      ) : null}

      <section style={{ marginTop: 14 }}>
        <h4 style={{ margin: "0 0 10px", color: "#0f172a" }}>
          {fr ? "Liste des personnages" : "Character list"}
        </h4>

        {visibleCharacters.length === 0 && !loading ? (
          <p className="ns-card-meta">
            {characters.length === 0
              ? fr
                ? "Aucun personnage n’a encore été ajouté."
                : "No characters have been added yet."
              : fr
              ? "Aucun personnage ne correspond à cette recherche."
              : "No character matches this search."}
          </p>
        ) : (
          <>
            {listedCharacters.map((character) => (
              <CharacterCard
                key={character.id || character._id || character.name}
                character={character}
                fr={fr}
                canEdit={mayManage}
                onEdit={startEdit}
                onArchive={archiveCharacter}
                onRestore={restoreCharacter}
              />
            ))}

            {hiddenCharacterCount > 0 ? (
              <div style={{ display: "flex", justifyContent: "center", marginTop: 10 }}>
                <button
                  type="button"
                  className="ns-button ns-button-outline"
                  onClick={() => setVisibleLimit((cur) => cur + CHARACTER_LIST_PAGE_SIZE)}
                >
                  {fr
                    ? `Afficher ${Math.min(CHARACTER_LIST_PAGE_SIZE, hiddenCharacterCount)} de plus`
                    : `Show ${Math.min(CHARACTER_LIST_PAGE_SIZE, hiddenCharacterCount)} more`}
                </button>
              </div>
            ) : null}
          </>
        )}
      </section>

      {mayManage ? (
        <form ref={formRef} onSubmit={saveCharacter} className="ns-card ns-card-soft" style={{ marginTop: 16 }}>
          <h4 style={{ marginTop: 0 }}>
            {editing
              ? fr
                ? `Modifier : ${editing.name || ""}`
                : `Edit: ${editing.name || ""}`
              : fr
              ? "Ajouter un personnage"
              : "Add character"}
          </h4>

          <div
            style={{
              display: "grid",
              gap: 14,
              gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
            }}
          >
            <CompactField label={`${fieldLabel("name", fr)} *`}>
              <input
                className="ns-input"
                value={form.name}
                onChange={(event) => updateField("name", event.target.value)}
                placeholder={fr ? "Ex. Ugo" : "Example: Ugo"}
              />
            </CompactField>

            <CompactField label={fieldLabel("role", fr)}>
              <input
                className="ns-input"
                value={form.role}
                onChange={(event) => updateField("role", event.target.value)}
                placeholder={fr ? "Ex. Personnage principal" : "Example: Main character"}
              />
            </CompactField>
          </div>

          <div style={{ display: "grid", gap: 16, marginTop: 16 }}>
            {LONG_FIELDS.map((key) => (
              <CompactField key={key} label={fieldLabel(key, fr)} help={fieldHelp(key, fr)}>
                <textarea
                  className="ns-input"
                  rows={key === "backstory" || key === "notes" ? 4 : 3}
                  value={form[key]}
                  onChange={(event) => updateField(key, event.target.value)}
                  style={{ width: "100%", minHeight: key === "importantRules" ? 110 : 90 }}
                />
              </CompactField>
            ))}
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 16 }}>
            <button type="submit" className="ns-button" disabled={saving}>
              {saving
                ? fr
                  ? "Enregistrement…"
                  : "Saving…"
                : editing
                ? fr
                  ? "Mettre à jour"
                  : "Update character"
                : fr
                ? "Ajouter le personnage"
                : "Add character"}
            </button>

            <button type="button" className="ns-button ns-button-outline" onClick={resetForm} disabled={saving}>
              {editing ? (fr ? "Annuler" : "Cancel") : fr ? "Effacer le formulaire" : "Clear form"}
            </button>
          </div>
        </form>
      ) : (
        <div className="ns-card ns-card-soft" style={{ marginTop: 14 }}>
          <p className="ns-card-meta" style={{ margin: 0 }}>
            {fr
              ? "Seul le propriétaire de l’histoire ou un administrateur peut modifier les personnages."
              : "Only the story owner or an admin can edit story characters."}
          </p>
        </div>
      )}
    </div>
  );
}
